import { faScaleBalanced } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { JobEventType } from '../../../__generated__/graphql';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
  Tabs,
  Textarea,
} from '../../components/ui';
import {
  useAdminServiceDisputesQuery,
  useJobTimelineQuery,
  useResolveJobDisputeMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { jobItemLabels, windowText, type ServiceJob } from '../ServiceGuestPage/serviceJob';
import { JobTimelineView } from '../ServiceJobPage/JobTimelineView';
import { liveEvents } from '../ServiceJobPage/jobTimeline';
import { formatMoney } from '../ServiceProviderPage/priceTables';
import { stayErrorMessage } from '../StayHostPage/stayErrors';

function ResolveModal({ job, onClose }: { job: ServiceJob; onClose: () => void }) {
  const timeline = useJobTimelineQuery({
    variables: { bookingId: job.id },
    fetchPolicy: 'network-only',
  });
  const [resolve, { loading }] = useResolveJobDisputeMutation({
    refetchQueries: ['AdminServiceDisputes'],
  });
  const total = Number(job.receipt.finalTotal ?? job.receipt.totalPrice ?? 0);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const info = job.serviceJob;
  const dispute = timeline.data
    ? liveEvents(timeline.data.jobTimeline.events).find(e => e.type === JobEventType.DisputeOpened)
    : null;

  const submit = async (refundAmount: number) => {
    if (!note.trim()) {
      setError('Write a note for the customer and the Provider');
      return;
    }
    if (!(refundAmount >= 0) || refundAmount > total) {
      setError(`Refund 0 to ${formatMoney(total)}`);
      return;
    }
    setError(null);
    try {
      await resolve({ variables: { bookingId: job.id, refundAmount, note: note.trim() } });
      toast.success('Review closed');
      onClose();
    } catch (e) {
      setError(stayErrorMessage(e, 'Could not close the review'));
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Review a job"
      modalWidth="min(860px, calc(100vw - 2rem))"
    >
      <Stack $gap={4}>
        <Strong>{jobItemLabels(info?.items).join(' + ') || 'Job'}</Strong>
        {info && (
          <Muted $size="sm">
            {windowText(info.windowStartAt, info.windowEndAt, info.timezone)}
          </Muted>
        )}
        {dispute && (
          <Alert tone="warning" title="The customer says">
            {String((dispute.payload as any)?.note ?? '')}
          </Alert>
        )}
        {timeline.data ? (
          <JobTimelineView
            timeline={timeline.data.jobTimeline}
            zone={info?.timezone ?? 'America/Phoenix'}
          />
        ) : (
          <LoadingBlock label="Loading the timeline" />
        )}
        <Field label="Note to both sides" htmlFor="resolve-note" required>
          <Textarea
            id="resolve-note"
            rows={3}
            maxLength={2000}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </Field>
        <Field label="Partial refund" htmlFor="resolve-amount" hint={`Paid: ${formatMoney(total)}`}>
          <Input
            id="resolve-amount"
            inputMode="decimal"
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />
        </Field>
        {error && (
          <Alert tone="error" title="Not saved">
            {error}
          </Alert>
        )}
        <InlineRow style={{ flexWrap: 'wrap' }}>
          <Button
            type="button"
            theme="danger"
            text="Refund in full"
            isLoading={loading}
            onClick={() => submit(total)}
          />
          <Button
            type="button"
            theme="secondary"
            text="Refund the amount"
            isLoading={loading}
            onClick={() => submit(Number(amount))}
          />
          <Button
            type="button"
            text="Keep the charge"
            isLoading={loading}
            onClick={() => submit(0)}
            dataTestId="resolve-keep"
          />
        </InlineRow>
      </Stack>
    </Modal>
  );
}

/**
 * Jobs a customer reported (spec 019 W22, D9): the timeline and photos, then a
 * full, partial or no refund (`resolveJobDispute`). Brand admins only.
 */
export default function AdminServiceJobsPage() {
  const [tab, setTab] = useState<'open' | 'resolved'>('open');
  const query = useAdminServiceDisputesQuery({
    variables: { status: tab === 'resolved' ? 'resolved' : null },
    fetchPolicy: 'network-only',
  });
  const [reviewing, setReviewing] = useState<ServiceJob | null>(null);
  const jobs = query.data?.adminServiceDisputes ?? [];
  return (
    <PageContainer width="lg" canvas data-testid="admin-service-jobs">
      <PageHeader
        eyebrow="Admin"
        title="Service jobs"
        description="Problems customers reported after a job."
      />
      <Stack $gap={6}>
        <Tabs
          items={[
            { key: 'open', label: 'Under review' },
            { key: 'resolved', label: 'Closed' },
          ]}
          value={tab}
          onChange={setTab}
        />
        {query.loading && !query.data ? (
          <LoadingBlock label="Loading" />
        ) : query.error && !query.data ? (
          <Alert tone="error" title="Could not load">
            {stayErrorMessage(query.error)}
          </Alert>
        ) : jobs.length === 0 ? (
          <EmptyState icon={faScaleBalanced} title="Nothing here" />
        ) : (
          jobs.map(job => (
            <Card key={job.id} data-testid={`dispute-${job.id}`}>
              <CardBody>
                <InlineRow style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <div>
                    <Strong>{jobItemLabels(job.serviceJob?.items).join(' + ') || 'Job'}</Strong>
                    <div>
                      <Muted $size="sm">
                        {[
                          job.serviceJob?.workerName,
                          job.guestDisplayName,
                          job.serviceJob?.site?.city,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Muted>
                    </div>
                  </div>
                  <InlineRow>
                    <Badge tone={tab === 'open' ? 'warning' : 'neutral'}>
                      {formatMoney(Number(job.receipt.finalTotal ?? job.receipt.totalPrice ?? 0))}
                    </Badge>
                    {tab === 'open' && (
                      <Button
                        type="button"
                        size="sm"
                        text="Review"
                        onClick={() => setReviewing(job)}
                      />
                    )}
                  </InlineRow>
                </InlineRow>
              </CardBody>
            </Card>
          ))
        )}
      </Stack>
      {reviewing && <ResolveModal job={reviewing} onClose={() => setReviewing(null)} />}
    </PageContainer>
  );
}
